package com.trevorwiebe.timesheet.invoicing.domain.model

import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import kotlinx.datetime.LocalDate

enum class InvoiceStatus { DRAFT, SENT }

enum class PaymentState { UNPAID, PARTIAL, PAID }

enum class InvoiceDisplayStatus(val title: String) {
    DRAFT("Draft"),
    OPEN("Open"),
    PARTIALLY_PAID("Partially Paid"),
    PAID("Paid"),
}

/**
 * One load on an invoice, copied from the load. A draft's lines follow later edits to its loads; a
 * sent invoice's lines only change when it's deliberately updated. Money is whole cents.
 */
data class InvoiceLine(
    val loadId: String,
    val loadNumber: String,
    val po: String,
    val date: LocalDate,
    val pickup: String,
    val drop: String,
    val description: String,
    val base: Int,
    val fuelSurcharge: Int = 0,
    val detention: Int = 0,
    val stopOff: Int = 0,
    val other: Int = 0,
    val fuelSurchargePercent: Double = 0.0,
) {
    val total: Int get() = base + fuelSurcharge + detention + stopOff + other
    val totalFormatted: String get() = "$" + total.formatCentsAsDollars()
}

data class Invoice(
    val id: String,
    val number: Int,
    val customerId: String,
    val customerName: String,
    val paymentTerms: String,
    val invoiceDate: LocalDate,
    val lines: List<InvoiceLine>,
    val status: InvoiceStatus = InvoiceStatus.DRAFT,
    val sentAt: LocalDate? = null,
    val sentTo: String = "",
    val amountPaid: Int = 0,
    // Storage path of the generated PDF; blank until it has been generated.
    val pdfPath: String = "",
    // Set when a sent invoice's lines were updated afterwards.
    val editedAt: LocalDate? = null,
) {
    val total: Int get() = lines.sumOf { it.total }
    val balance: Int get() = total - amountPaid
    val loadIds: List<String> get() = lines.map { it.loadId }

    val paymentState: PaymentState
        get() = when {
            amountPaid <= 0 -> PaymentState.UNPAID
            balance > 0 -> PaymentState.PARTIAL
            else -> PaymentState.PAID
        }

    val numberLabel: String get() = "#$number"
    val totalFormatted: String get() = "$" + total.formatCentsAsDollars()
    val balanceFormatted: String get() = "$" + balance.formatCentsAsDollars()
    val isDraft: Boolean get() = status == InvoiceStatus.DRAFT

    val displayStatus: InvoiceDisplayStatus
        get() = when {
            status == InvoiceStatus.DRAFT -> InvoiceDisplayStatus.DRAFT
            paymentState == PaymentState.PAID -> InvoiceDisplayStatus.PAID
            paymentState == PaymentState.PARTIAL -> InvoiceDisplayStatus.PARTIALLY_PAID
            else -> InvoiceDisplayStatus.OPEN
        }

    val statusLabel: String
        get() = when {
            status == InvoiceStatus.DRAFT -> "Draft"
            paymentState == PaymentState.PAID -> "Paid"
            paymentState == PaymentState.PARTIAL -> "Partly paid • $balanceFormatted due"
            else -> "Sent • unpaid"
        }
}
